import React from "react";
import moment from "moment";
import { Container } from "./container";
import { Loader } from "./loaders";
import ServiceFactory from "../service";
import { getSsoTenantConfig, ssoEligible } from "../auth/sso";
import CUSTOM_MODULES from "../custom-modules";

// ─────────────────────────────────────────────────────────────────
// Tenant modules — registry-driven per-tenant widgets.
//
// The broker delivers tenantConfig.modules (from PlunkTenantConfig's
// Modules manifest) on every SSO session. Pages render
// <TenantModules page="acquisition" range={range} /> and any manifest
// entries matching that page slot appear — no tenant conditionals in
// code, no deploy to give a tenant a new module (admin.plunk.io edits
// the manifest). First generic component: keyvalue-leaderboard.
//
// Manifest entry shape:
//   { id, component: 'keyvalue-leaderboard', page: 'acquisition',
//     title, config: { type: 'Engagement', name: 'Search',
//                      keyName: 'Fairfield.Search.Term',
//                      top: 10, minLength: 3, excludeEmails: true } }
// ─────────────────────────────────────────────────────────────────

// range token (this_month, last_week, …) → { startDate, endDate } for the
// /metrics/keyvalues/{type}/{name}/{startDate}/{endDate} route.
// endDate is sent as the NEXT day: the endpoint's SQL is
// `Date BETWEEN start AND end` and MySQL reads a bare end date as 00:00:00 —
// an endDate of "today" excluded everything that happened today (the
// leaderboard only moved at midnight; bit Fairfield 2026-07-10).
export function rangeDates(range) {
  const fmt = m => m.format("YYYY-MM-DD");
  const fmtEnd = m => moment(m).add(1, "day").format("YYYY-MM-DD");
  const now = moment();
  switch (range) {
    case "today":
      return {
        startDate: fmt(now),
        endDate: fmtEnd(now)
      };
    case "yesterday":
      {
        const y = moment().subtract(1, "days");
        return {
          startDate: fmt(y),
          endDate: fmtEnd(y)
        };
      }
    case "last_7_days":
      {
        const s7 = moment().subtract(7, "days");
        return {
          startDate: fmt(s7),
          endDate: fmtEnd(now)
        };
      }
    case "all_time":
      return {
        startDate: "2018-01-01",
        endDate: fmtEnd(now)
      };
    case "this_week":
      return {
        startDate: fmt(moment().startOf("week")),
        endDate: fmtEnd(now)
      };
    case "last_week":
      {
        const s = moment().subtract(1, "week").startOf("week");
        return {
          startDate: fmt(s),
          endDate: fmtEnd(moment(s).endOf("week"))
        };
      }
    case "last_month":
      {
        const s = moment().subtract(1, "month").startOf("month");
        return {
          startDate: fmt(s),
          endDate: fmtEnd(moment(s).endOf("month"))
        };
      }
    case "this_quarter":
      return {
        startDate: fmt(moment().startOf("quarter")),
        endDate: fmtEnd(now)
      };
    case "last_quarter":
      {
        const s = moment().subtract(1, "quarter").startOf("quarter");
        return {
          startDate: fmt(s),
          endDate: fmtEnd(moment(s).endOf("quarter"))
        };
      }
    case "this_year":
      return {
        startDate: fmt(moment().startOf("year")),
        endDate: fmtEnd(now)
      };
    case "last_year":
      {
        const s = moment().subtract(1, "year").startOf("year");
        return {
          startDate: fmt(s),
          endDate: fmtEnd(moment(s).endOf("year"))
        };
      }
    case "this_month":
    default:
      return {
        startDate: fmt(moment().startOf("month")),
        endDate: fmtEnd(now)
      };
  }
}

// ── Generic component: top-N leaderboard over engagement key-values ──
class KeyValueLeaderboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      rows: null,
      error: false
    };
    this._alive = false;
  }
  componentDidMount() {
    this._alive = true;
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  componentDidUpdate(prevProps) {
    if (prevProps.range !== this.props.range) this.load();
  }
  async load() {
    const config = this.props.config || {};
    let {
      startDate,
      endDate
    } = rangeDates(this.props.range);
    const type = config.type || "Engagement";
    const name = config.name || "Search";

    // INTERIM payload guard (2026-07-28): the keyvalues endpoint returns
    // RAW kv rows — Fairfield's per-keystroke search tracking hit ~1 MB
    // per TWO DAYS, so month+ windows exceed the Lambda 6 MB response
    // cap and 502. Until the server-side top-N aggregation endpoint
    // exists (ask filed in PRODUCT_PERFORMANCE spec's grid callout
    // sibling — Service.Activity), fetch the window in ≤3-day slices
    // and aggregate client-side; ranges longer than 31 days truncate to
    // the trailing 31 (labeled in the meta line via state.truncated).
    let truncated = false;
    const end = moment(endDate, "YYYY-MM-DD");
    let start = moment(startDate, "YYYY-MM-DD");
    if (end.diff(start, "days") > 31) {
      start = moment(end).subtract(31, "days");
      truncated = true;
    }
    const slices = [];
    let cur = moment(start);
    while (cur.isBefore(end)) {
      const sliceEnd = moment.min(moment(cur).add(3, "days"), end);
      slices.push([cur.format("YYYY-MM-DD"), sliceEnd.format("YYYY-MM-DD")]);
      cur = sliceEnd;
    }
    try {
      const results = await Promise.all(slices.map(([s1, s2]) => {
        const api = `/metrics/keyvalues/${type}/${name}/${s1}/${s2}`;
        const Service = ServiceFactory.getType(api);
        const service = new Service();
        return service.getAsync().catch(() => null);
      }));
      if (results.every(r => r === null)) throw new Error("all slices failed");
      const counts = {};
      results.forEach(data => {
        const result = data && data.data && data.data.result || [];
        result.forEach(r => {
          if (config.keyName && r.keyName !== config.keyName) return;
          let v = (r.keyValue || "").trim().toLowerCase();
          if (!v) return;
          if (config.minLength && v.length < config.minLength) return;
          if (config.excludeEmails && v.indexOf("@") !== -1) return;
          counts[v] = (counts[v] || 0) + 1;
        });
      });
      const rows = Object.keys(counts).map(k => ({
        value: k,
        hits: counts[k]
      })).sort((a, b) => b.hits - a.hits).slice(0, config.top || 10);
      if (this._alive) this.setState({
        rows,
        error: false,
        truncated
      });
    } catch (e) {
      if (this._alive) this.setState({
        rows: [],
        error: true
      });
    }
  }
  render() {
    const {
      rows,
      error
    } = this.state;
    if (rows === null) return /*#__PURE__*/React.createElement(Loader, null);
    if (error) return /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "Data unavailable.");
    if (!rows.length) return /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "No data for this range.");
    const max = rows[0].hits || 1;
    const note = this.state.truncated ? /*#__PURE__*/React.createElement("p", {
      className: "module-empty",
      style: {
        marginTop: "6px"
      }
    }, "Showing the most recent 31 days of the selected range.") : null;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("table", {
      className: "tenant-module-leaderboard"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      className: "rank"
    }, "#"), /*#__PURE__*/React.createElement("th", null, "Term"), /*#__PURE__*/React.createElement("th", {
      className: "hits"
    }, "Hits"), /*#__PURE__*/React.createElement("th", {
      className: "bar-col"
    }))), /*#__PURE__*/React.createElement("tbody", null, rows.map((r, i) => /*#__PURE__*/React.createElement("tr", {
      key: r.value
    }, /*#__PURE__*/React.createElement("td", {
      className: "rank"
    }, i + 1), /*#__PURE__*/React.createElement("td", {
      className: "term"
    }, r.value), /*#__PURE__*/React.createElement("td", {
      className: "hits"
    }, r.hits), /*#__PURE__*/React.createElement("td", {
      className: "bar-col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bar",
      style: {
        width: `${Math.max(4, Math.round(r.hits / max * 100))}%`
      }
    })))))), note);
  }
}

// Generic (config-driven) module types. Bespoke React components live in
// ../custom-modules and are referenced as "custom:<key>". Either way the
// registry manifest is the ONLY activation switch — code ships dormant.
const MODULE_COMPONENTS = {
  "keyvalue-leaderboard": KeyValueLeaderboard
};
function resolveComponent(name) {
  if (!name) return null;
  if (name.indexOf("custom:") === 0) return CUSTOM_MODULES[name.slice(7)] || null;
  return MODULE_COMPONENTS[name] || null;
}
export function getManifest() {
  let cfg = null;
  try {
    cfg = getSsoTenantConfig();
  } catch (e) {/* no session config */}
  if (cfg && Array.isArray(cfg.modules)) return cfg.modules;
  // Localhost dev only (never *.plunk.io): direct-Cognito sessions carry
  // no broker tenantConfig, so registry module manifests are invisible.
  // Paste a manifest to test locally, e.g.:
  //   localStorage.plunkDevModules = JSON.stringify([{id, component, placement, title, config}])
  if (!ssoEligible()) {
    try {
      const raw = localStorage.getItem("plunkDevModules");
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr;
      }
    } catch (e) {/* malformed override — ignore */}
  }
  return [];
}

// Placement:
//   { placement: { type: "widget", page: "acquisition" } }   → slot on an existing page
//   { placement: { type: "page", menu: { label, icon } } }   → standalone /Modules/:id + nav entry
//   legacy flat `page: "acquisition"`                        → shorthand for the widget form
function isWidgetFor(m, page) {
  if (!m) return false;
  if (m.placement && m.placement.type === "widget") return m.placement.page === page;
  if (m.placement && m.placement.type === "page") return false;
  return m.page === page;
}

/** Whether any widget is set up for this page slot (the Dashboard's "Workspace modules" card uses it). */
export function hasWidgets(page) {
  return getManifest().some(m => isWidgetFor(m, page) && resolveComponent(m.component));
}

// Page-type entries with a resolvable component — consumed by the router
// (/Modules/:id) and the sidebar menu.
export function getPageModules() {
  return getManifest().filter(m => m && m.placement && m.placement.type === "page" && resolveComponent(m.component));
}

// Full-page renderer behind the /Modules/:id route.
export function ModulePage({
  params
}) {
  const id = params && params.id;
  const entry = getPageModules().filter(m => (m.id || "") === id)[0];
  if (!entry) return /*#__PURE__*/React.createElement("p", {
    className: "module-empty"
  }, "No such module for this account.");
  const Comp = resolveComponent(entry.component);
  return /*#__PURE__*/React.createElement("div", {
    className: "tenant-modules tenant-module-page"
  }, /*#__PURE__*/React.createElement(Container, {
    title: entry.title || entry.id,
    css: "stacked tenant-module"
  }, /*#__PURE__*/React.createElement(Comp, {
    config: entry.config || {},
    range: "this_month"
  })));
}
export default function TenantModules({
  page,
  range
}) {
  const active = getManifest().filter(m => isWidgetFor(m, page) && resolveComponent(m.component));
  if (!active.length) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: "tenant-modules"
  }, active.map(m => {
    const Comp = resolveComponent(m.component);
    return /*#__PURE__*/React.createElement(Container, {
      key: m.id || m.title,
      title: m.title || m.id,
      css: "stacked tenant-module"
    }, /*#__PURE__*/React.createElement(Comp, {
      config: m.config || {},
      range: range
    }));
  }));
}